// Canonical public route and content manifest. Runtime routing, prerendering,
// sitemap generation, RSS, and route tests all consume this inventory.

import { SITE_URL } from "../content/site";

export { SITE_URL };

export type RouteEntry = {
  path: string;
  changefreq: "weekly" | "monthly" | "yearly";
  priority: number;
};

export type SchemaEligibility = "webpage" | "collection" | "service" | "article" | "about" | "contact" | "none";

export type ClaimId =
  | "claim-founder-experience"
  | "claim-work-engagements"
  | "claim-delivery-method"
  | "claim-scorecard-heuristic"
  | "claim-contact-intake"
  | "claim-canada-first"
  | "claim-production-method"
  | "claim-ai-verification-review"
  | "claim-website-data-handling";

export type RouteContentManifestEntry = RouteEntry & {
  id: string;
  pageRole: "home" | "service-hub" | "entry-engagement" | "service" | "resource" | "assessment" | "solution-hub" | "solution" | "industry-hub" | "industry" | "proof" | "company" | "contact" | "privacy" | "insights-hub";
  searchIntent: string;
  contentKey: string;
  seoKey: string;
  primaryCtaId: "discuss" | "verification-review" | "scorecard" | "proof" | "none";
  proofRefs: readonly string[];
  claimRefs: readonly ClaimId[];
  schemaEligibility: SchemaEligibility;
};

const CONTENT_MANIFEST_BASE = {
  home: {
    id: "home", path: "/", changefreq: "weekly", priority: 1,
    pageRole: "home", searchIntent: "production AI workflow systems partner in Canada",
    contentKey: "home", seoKey: "home", primaryCtaId: "discuss",
    proofRefs: ["work-engagements", "founder-experience"], claimRefs: ["claim-work-engagements", "claim-founder-experience", "claim-delivery-method", "claim-canada-first", "claim-production-method"], schemaEligibility: "webpage",
  },
  services: {
    id: "services", path: "/services", changefreq: "monthly", priority: 0.9,
    pageRole: "service-hub", searchIntent: "ways to engage a production AI implementation partner",
    contentKey: "services", seoKey: "services", primaryCtaId: "discuss",
    proofRefs: ["delivery-method"], claimRefs: ["claim-delivery-method", "claim-production-method"], schemaEligibility: "collection",
  },
  serviceProductionAI: {
    id: "service-production-ai", path: "/services/production-ai-systems", changefreq: "monthly", priority: 0.9,
    pageRole: "service", searchIntent: "build and deploy a production AI workflow system",
    contentKey: "production-ai-systems", seoKey: "production-ai-systems", primaryCtaId: "discuss",
    proofRefs: ["work-engagements", "delivery-method"], claimRefs: ["claim-delivery-method", "claim-production-method"], schemaEligibility: "service",
  },
  verificationReview: {
    id: "ai-verification-review", path: "/services/ai-verification-review", changefreq: "monthly", priority: 0.9,
    pageRole: "entry-engagement", searchIntent: "independent AI verification and production readiness review before funding launch or scale",
    contentKey: "aiVerificationReview", seoKey: "aiVerificationReview", primaryCtaId: "verification-review",
    proofRefs: ["delivery-method"], claimRefs: ["claim-delivery-method", "claim-production-method", "claim-ai-verification-review"], schemaEligibility: "service",
  },
  verificationSample: {
    id: "ai-verification-review-sample", path: "/resources/sample-ai-verification-review", changefreq: "monthly", priority: 0.7,
    pageRole: "resource", searchIntent: "illustrative AI verification decision memo and production readiness review sample",
    contentKey: "aiVerificationReviewSample", seoKey: "aiVerificationReviewSample", primaryCtaId: "verification-review",
    proofRefs: [], claimRefs: ["claim-production-method", "claim-ai-verification-review"], schemaEligibility: "webpage",
  },
  serviceData: {
    id: "service-data-foundations", path: "/services/data-platforms", changefreq: "monthly", priority: 0.85,
    pageRole: "service", searchIntent: "prepare reliable governed data for production AI",
    contentKey: "data-platforms", seoKey: "data-platforms", primaryCtaId: "discuss",
    proofRefs: ["work-engagements", "delivery-method"], claimRefs: ["claim-delivery-method", "claim-production-method"], schemaEligibility: "service",
  },
  serviceDecision: {
    id: "service-decision-systems", path: "/services/decision-intelligence", changefreq: "monthly", priority: 0.85,
    pageRole: "service", searchIntent: "build forecasting scoring and decision workflow systems",
    contentKey: "decision-intelligence", seoKey: "decision-intelligence", primaryCtaId: "discuss",
    proofRefs: ["work-engagements", "delivery-method"], claimRefs: ["claim-delivery-method", "claim-production-method"], schemaEligibility: "service",
  },
  scorecard: {
    id: "ai-readiness-scorecard", path: "/ai-readiness-scorecard", changefreq: "monthly", priority: 0.8,
    pageRole: "assessment", searchIntent: "directional production AI readiness assessment",
    contentKey: "aiReadinessScorecard", seoKey: "aiReadinessScorecard", primaryCtaId: "scorecard",
    proofRefs: [], claimRefs: ["claim-scorecard-heuristic"], schemaEligibility: "webpage",
  },
  solutions: {
    id: "solutions", path: "/solutions", changefreq: "monthly", priority: 0.8,
    pageRole: "solution-hub", searchIntent: "production AI solution types and workflow patterns",
    contentKey: "solutionsHub", seoKey: "solutions", primaryCtaId: "discuss",
    proofRefs: ["delivery-method"], claimRefs: ["claim-delivery-method", "claim-production-method"], schemaEligibility: "collection",
  },
  solutionWorkflow: {
    id: "solution-workflow-automation", path: "/solutions/ai-agents-workflow-automation", changefreq: "monthly", priority: 0.8,
    pageRole: "solution", searchIntent: "bounded AI agents and workflow automation",
    contentKey: "aiAgents", seoKey: "aiAgents", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-production-method"], schemaEligibility: "service",
  },
  solutionDocuments: {
    id: "solution-document-intelligence", path: "/solutions/document-intelligence", changefreq: "monthly", priority: 0.8,
    pageRole: "solution", searchIntent: "document extraction validation and exception workflows",
    contentKey: "documentIntelligence", seoKey: "documentIntelligence", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-production-method"], schemaEligibility: "service",
  },
  solutionKnowledge: {
    id: "solution-knowledge-retrieval", path: "/solutions/knowledge-systems-enterprise-retrieval", changefreq: "monthly", priority: 0.8,
    pageRole: "solution", searchIntent: "governed enterprise knowledge retrieval with citations",
    contentKey: "knowledgeSystems", seoKey: "knowledgeSystems", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-production-method"], schemaEligibility: "service",
  },
  solutionSupport: {
    id: "solution-customer-support", path: "/solutions/customer-support-service-systems", changefreq: "monthly", priority: 0.8,
    pageRole: "solution", searchIntent: "customer support triage resolution and human handoff systems",
    contentKey: "customerSupport", seoKey: "customerSupport", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-production-method"], schemaEligibility: "service",
  },
  solutionPredictive: {
    id: "solution-predictive-decisions", path: "/solutions/predictive-models-decision-science", changefreq: "monthly", priority: 0.8,
    pageRole: "solution", searchIntent: "predictive models embedded in recurring business decisions",
    contentKey: "predictiveModels", seoKey: "predictiveModels", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-production-method"], schemaEligibility: "service",
  },
  industries: {
    id: "industries", path: "/industries", changefreq: "monthly", priority: 0.8,
    pageRole: "industry-hub", searchIntent: "production AI for commerce operations",
    contentKey: "industriesHub", seoKey: "industries", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-work-engagements", "claim-production-method"], schemaEligibility: "collection",
  },
  industryRetail: {
    id: "industry-retail-ecommerce", path: "/industries/retail-ecommerce-ai", changefreq: "monthly", priority: 0.8,
    pageRole: "industry", searchIntent: "production AI workflows for retail and ecommerce operations",
    contentKey: "retail-ecommerce-ai", seoKey: "retail-ecommerce-ai", primaryCtaId: "discuss",
    proofRefs: ["work-engagements", "founder-experience"], claimRefs: ["claim-work-engagements", "claim-founder-experience", "claim-production-method"], schemaEligibility: "service",
  },
  industryCpg: {
    id: "industry-cpg", path: "/industries/cpg-ai", changefreq: "monthly", priority: 0.8,
    pageRole: "industry", searchIntent: "production AI workflows for consumer goods operations",
    contentKey: "cpg-ai", seoKey: "cpg-ai", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-work-engagements", "claim-production-method"], schemaEligibility: "service",
  },
  industryMultiLocation: {
    id: "industry-franchise-multilocation", path: "/industries/franchise-multi-location-ai", changefreq: "monthly", priority: 0.8,
    pageRole: "industry", searchIntent: "production AI workflows for franchise and multi-location operations",
    contentKey: "franchise-multi-location-ai", seoKey: "franchise-multi-location-ai", primaryCtaId: "discuss",
    proofRefs: ["work-engagements"], claimRefs: ["claim-work-engagements", "claim-production-method"], schemaEligibility: "service",
  },
  work: {
    id: "work", path: "/work", changefreq: "monthly", priority: 0.8,
    pageRole: "proof", searchIntent: "Vert AI delivery evidence and production controls",
    contentKey: "work", seoKey: "work", primaryCtaId: "proof",
    proofRefs: ["work-engagements", "founder-experience", "delivery-method"], claimRefs: ["claim-work-engagements", "claim-founder-experience", "claim-delivery-method", "claim-production-method"], schemaEligibility: "webpage",
  },
  about: {
    id: "about", path: "/about", changefreq: "monthly", priority: 0.7,
    pageRole: "company", searchIntent: "Vert AI founder-led delivery model",
    contentKey: "about", seoKey: "about", primaryCtaId: "discuss",
    proofRefs: ["founder-experience", "delivery-method"], claimRefs: ["claim-founder-experience", "claim-delivery-method", "claim-canada-first", "claim-production-method"], schemaEligibility: "about",
  },
  contact: {
    id: "contact", path: "/contact", changefreq: "monthly", priority: 0.8,
    pageRole: "contact", searchIntent: "discuss a production AI workflow or decision",
    contentKey: "contact", seoKey: "contact", primaryCtaId: "discuss",
    proofRefs: [], claimRefs: ["claim-contact-intake", "claim-delivery-method", "claim-canada-first", "claim-website-data-handling"], schemaEligibility: "contact",
  },
  privacy: {
    id: "privacy", path: "/privacy", changefreq: "yearly", priority: 0.3,
    pageRole: "privacy", searchIntent: "Vert AI website privacy information",
    contentKey: "privacy", seoKey: "privacy", primaryCtaId: "none",
    proofRefs: [], claimRefs: ["claim-website-data-handling", "claim-contact-intake", "claim-scorecard-heuristic"], schemaEligibility: "webpage",
  },
  insights: {
    id: "insights", path: "/insights", changefreq: "weekly", priority: 0.8,
    pageRole: "insights-hub", searchIntent: "operator guidance for production AI decisions",
    contentKey: "insights", seoKey: "insights", primaryCtaId: "discuss",
    proofRefs: ["delivery-method"], claimRefs: ["claim-production-method"], schemaEligibility: "collection",
  },
} as const satisfies Record<string, RouteContentManifestEntry>;

const MANIFEST_H1: Record<string, { en: string; fr: string }> = {
  home: {
    en: "Fix the costly workflows slowing down commerce operations.",
    fr: "Corrigez les processus coûteux qui ralentissent vos opérations de commerce.",
  },
  services: {
    en: "One decision review. Four implementation paths.",
    fr: "Un examen décisionnel. Quatre parcours de mise en œuvre.",
  },
  "service-production-ai": {
    en: "Turn a high-value workflow into a controlled production system.",
    fr: "Transformez un processus à forte valeur en système de production contrôlé.",
  },
  "ai-verification-review": {
    en: "Verify your AI initiative before you fund, launch, or scale it.",
    fr: "Vérifiez votre initiative d’IA avant de la financer, de la lancer ou de l’élargir.",
  },
  "ai-verification-review-sample": {
    en: "See how evidence changes an AI scale decision.",
    fr: "Voyez comment les preuves changent une décision d’expansion en IA.",
  },
  "service-data-foundations": {
    en: "Build the trusted data foundation your AI use case actually needs.",
    fr: "Construisez la fondation de données fiable dont votre cas d’usage d’IA a réellement besoin.",
  },
  "service-decision-systems": {
    en: "Turn a recurring decision into a measured operating system.",
    fr: "Transformez une décision récurrente en système opérationnel mesurable.",
  },
  "ai-readiness-scorecard": {
    en: "Is this workflow ready for production AI?",
    fr: "Ce processus est-il prêt pour l’IA en production?",
  },
  solutions: {
    en: "Five ways to fix costly work without losing control.",
    fr: "Cinq façons de corriger un travail coûteux sans perdre le contrôle.",
  },
  "solution-workflow-automation": {
    en: "Move multi-step work out of inboxes without giving AI open-ended authority.",
    fr: "Sortez le travail en plusieurs étapes des boîtes de réception sans donner carte blanche à l’IA.",
  },
  "solution-document-intelligence": {
    en: "Turn document-heavy work into validated, traceable action.",
    fr: "Transformez le travail documentaire en actions validées et traçables.",
  },
  "solution-knowledge-retrieval": {
    en: "Give teams reliable answers from approved knowledge—with evidence.",
    fr: "Donnez aux équipes des réponses fiables, fondées sur des connaissances approuvées et des preuves à l’appui.",
  },
  "solution-customer-support": {
    en: "Reduce service effort without losing policy or escalation control.",
    fr: "Réduisez l’effort de service sans perdre le contrôle des politiques ni des transferts.",
  },
  "solution-predictive-decisions": {
    en: "Turn forecasts and scores into governed recurring decisions.",
    fr: "Transformez les prévisions et les cotes en décisions récurrentes gouvernées.",
  },
  industries: {
    en: "Industry provides the context. The workflow determines the system.",
    fr: "Le secteur fournit le contexte. Le processus détermine le système.",
  },
  "industry-retail-ecommerce": {
    en: "Turn retail and ecommerce signals into governed operating decisions.",
    fr: "Transformer les signaux du commerce de détail et du commerce électronique en décisions opérationnelles gouvernées.",
  },
  "industry-cpg": {
    en: "Make CPG planning decisions traceable across fragmented customer data.",
    fr: "Rendre les décisions de planification traçables malgré la fragmentation des données clients.",
  },
  "industry-franchise-multilocation": {
    en: "Turn location variance into governed operating follow-up.",
    fr: "Transformer les écarts entre établissements en suivis opérationnels gouvernés.",
  },
  work: {
    en: "Four engagements. The operating problem, intervention, controls, and evidence boundary.",
    fr: "Quatre mandats. Le problème opérationnel, l’intervention, les contrôles et les limites de la preuve.",
  },
  about: {
    en: "Senior accountability from the first workflow decision through production.",
    fr: "Une responsabilité au plus haut niveau, de la première décision sur le processus jusqu’à la production.",
  },
  contact: {
    en: "Bring one costly workflow or recurring decision.",
    fr: "Présentez un processus coûteux ou une décision récurrente.",
  },
  privacy: {
    en: "Website privacy",
    fr: "Confidentialité du site Web",
  },
  insights: {
    en: "Make the next AI decision with evidence, not momentum.",
    fr: "Prenez la prochaine décision en IA à partir de preuves, pas d’un effet d’entraînement.",
  },
};

export type LocalizedManifestEntry = RouteContentManifestEntry & {
  paths: { en: string; fr: string };
  locales: { en: "en-CA"; fr: "fr-CA" };
  h1: { en: string; fr: string };
  metadata: {
    en: { locale: "en-CA"; key: string };
    fr: { locale: "fr-CA"; key: string };
  };
};

/**
 * Canonical content inventory keyed by stable content ID. Metadata values are
 * resolved through `seoKey` in the relevant localized content source so copy
 * is not duplicated between the manifest and the rendering system.
 */
export const CONTENT_MANIFEST = Object.fromEntries(
  Object.values(CONTENT_MANIFEST_BASE).map((entry) => [
    entry.id,
    {
      ...entry,
      paths: {
        en: entry.path,
        fr: entry.path === "/" ? "/fr" : `/fr${entry.path}`,
      },
      locales: { en: "en-CA", fr: "fr-CA" },
      h1: MANIFEST_H1[entry.id],
      metadata: {
        en: { locale: "en-CA", key: entry.seoKey },
        fr: { locale: "fr-CA", key: entry.seoKey },
      },
    },
  ]),
) as Record<string, LocalizedManifestEntry>;

export const STATIC_ROUTES: RouteEntry[] = Object.values(CONTENT_MANIFEST).map(
  ({ path, changefreq, priority }) => ({ path, changefreq, priority }),
);

export const REDIRECT_ROUTES = [
  { from: "/home", to: "/", reason: "Duplicate homepage path." },
  { from: "/services/ai-readiness-audit", to: "/services/ai-verification-review", reason: "The AI Verification Review replaces the legacy readiness and opportunity-mapping offer." },
  { from: "/services/agentic-ai-workflows", to: "/services/production-ai-systems", reason: "Agentic AI is a solution type within the production AI engagement." },
  { from: "/services/agentic-ai", to: "/services/production-ai-systems", reason: "Legacy service path now resolves directly to the canonical production AI engagement." },
  { from: "/services/generative-ai", to: "/services/production-ai-systems", reason: "Legacy service path now resolves directly to the canonical production AI engagement." },
  { from: "/services/predictive-ai", to: "/services/decision-intelligence", reason: "Legacy predictive AI service path." },
  { from: "/solutions/data-platforms-for-ai", to: "/services/data-platforms", reason: "Data foundations are an engagement path, not a competing solution page." },
  { from: "/industries/professional-services-ai", to: "/solutions/knowledge-systems-enterprise-retrieval", reason: "Consolidated overlapping industry intent into the relevant solution." },
  { from: "/industries/b2b-services-ai", to: "/solutions/ai-agents-workflow-automation", reason: "Consolidated overlapping industry intent into the relevant solution." },
  { from: "/industries/accounting-firms", to: "/solutions/document-intelligence", reason: "Consolidated overlapping industry intent into the relevant solution." },
  { from: "/industries/manufacturing-distribution", to: "/solutions/predictive-models-decision-science", reason: "Consolidated overlapping industry intent into the relevant solution." },
  { from: "/industries/service-businesses", to: "/solutions/knowledge-systems-enterprise-retrieval", reason: "Legacy path resolves directly to the final canonical solution." },
  { from: "/industries/ecommerce-operations", to: "/industries/retail-ecommerce-ai", reason: "Legacy ecommerce path." },
  { from: "/insights/choose-first-ai-workflow-to-automate", to: "/insights/workflows-deserve-ai", reason: "Merged workflow-selection guidance." },
  { from: "/insights/ai-theatre-is-expensive", to: "/insights/ai-pilots-fail-before-production", reason: "Merged pilot-failure analysis." },
  { from: "/insights/ai-procurement-trap-vendor-lockin-2026", to: "/insights/vet-ai-vendor", reason: "Merged vendor evaluation guidance." },
  { from: "/insights/enterprise-ai-fails-in-production", to: "/insights/production-grade-ai", reason: "Merged production-readiness analysis." },
  { from: "/insights/model-to-production-engineering-gap", to: "/insights/production-grade-ai", reason: "Merged production-engineering analysis." },
  { from: "/insights/data-not-ready-for-ai", to: "/insights/production-ai-readiness-checklist-mid-market", reason: "Merged data-readiness guidance." },
  { from: "/insights/hidden-cost-layer-ai-roi", to: "/insights/measure-ai-roi-before-building", reason: "Merged AI economics guidance." },
  { from: "/insights/decision-layers-missing-architecture", to: "/insights/dashboard-to-decision-engine-production", reason: "Merged decision-layer architecture guidance." },
  { from: "/insights/etl-to-ai-native-data-stack", to: "/insights/data-foundation-behind-useful-ai-systems", reason: "Merged data-foundation guidance." },
  { from: "/insights/growth-metrics-are-lying", to: "/insights/decision-intelligence-vs-business-intelligence", reason: "Merged decision-quality guidance." },
  { from: "/insights/capital-allocation-data-problem", to: "/insights/decision-intelligence-vs-business-intelligence", reason: "Merged decision-quality guidance." },
] as const;

export const NOINDEX_ROUTES = [
  { path: "/apply/ai-verification-review", reason: "Executive qualification intake; intentionally noindex and reached through Review CTAs." },
  { path: "/unsubscribe", reason: "Utility route for email preference management." },
  { path: "*", reason: "404 fallback page." },
] as const;

export const ARTICLE_SLUGS: string[] = [
  "production-grade-ai",
  "workflows-deserve-ai",
  "vet-ai-vendor",
  "ai-pilots-fail-before-production",
  "production-ai-readiness-checklist-mid-market",
  "decision-intelligence-vs-business-intelligence",
  "ai-governance-monitoring-evaluation-human-review",
  "what-agentic-ai-should-automate",
  "data-foundation-behind-useful-ai-systems",
  "measure-ai-roi-before-building",
  "dashboard-to-decision-engine-production",
  "human-in-the-loop-economics-2026",
  "evaluation-gap-honest-ai-scorecard",
  "build-vs-buy-ai-2026-decision-framework",
  "semantic-layer-undervalued-asset",
  "agentic-ai-production-failures",
];

export const absoluteUrl = (path: string) => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${clean === "/" ? "" : clean}`;
};
